'use client';

import React, { type FC, type ReactNode, useEffect, useState } from 'react';

interface ConsentState {
    marketing: 'granted' | 'denied';
    statistics: 'granted' | 'denied';
    preferences: 'granted' | 'denied';
}

interface CookieConsentScriptProps {
    children: (consent: ConsentState) => ReactNode;
}

const getParsedConsent = (): ConsentState | null => {
    try {
        const saved = localStorage.getItem('consentGranted');

        if (!saved) {
            return null;
        }

        return JSON.parse(window.atob(saved)) as ConsentState;
    } catch {
        return null;
    }
};

export const CookieConsentScript: FC<CookieConsentScriptProps> = ({ children }) => {
    const [consent, setConsent] = useState<ConsentState | null>(null);

    const renderScript = (): void => {
        const botPattern =
            /Googlebot|Google-InspectionTool|GoogleOther|Google-Extended|bingbot|BingPreview|Slurp|DuckDuckBot|Baiduspider|YandexBot|GPTBot|ChatGPT-User|ClaudeBot|PerplexityBot|Applebot|Applebot-Extended|Amazonbot|OAI-SearchBot/i;

        if (
            !botPattern.test(window.navigator.userAgent) &&
            !document.querySelector('script[src="https://cdn.wniosker.pl/js/cookie-consent.js"]')
        ) {
            const script = document.createElement('script');
            script.src = 'https://cdn.wniosker.pl/js/cookie-consent.js';
            script.async = true;

            document.body.append(script);
        }
    };

    useEffect(() => {
        const checkConsent = (): void => {
            const updatedConsent = getParsedConsent();

            if (updatedConsent) {
                setConsent(updatedConsent);
            }
        };

        checkConsent();

        window.addEventListener('grantCookieConsent', checkConsent);

        renderScript();

        return (): void => {
            window.removeEventListener('grantCookieConsent', checkConsent);
        };
    }, []);

    return <>{consent && children(consent)}</>;
};
